(() => {
  'use strict';

  if (window.__GBJ_SHOP_PAY_GIFT_BUTTON__) return;
  window.__GBJ_SHOP_PAY_GIFT_BUTTON__ = true;

  const GIFT_ID = 51302924615847;
  const DEFAULT_THRESHOLD_CENTS = 79900;
  const GIFT_PROPERTY = '_free_gift';
  const REQUEST_TIMEOUT_MS = 6000;
  // Open the store checkout with its express-payment choices after gift verification.
  const FORCE_STANDARD_CHECKOUT_TEST = true;
  let checkoutDeadline = 0;
  const EXPRESS_ITEM_KEY = 'gbjExpressCheckoutItem';
  const RETURN_RELOAD_KEY = 'gbjCheckoutReturnReload';
  const ROOT = `${window.Shopify?.routes?.root || '/'}`.replace(/\/?$/, '/');

  const cartUrl = (path) => `${ROOT}${String(path).replace(/^\//, '')}`;
  const wait = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));
  // Checkout must not depend on storage permission (restricted Safari sessions
  // can throw when accessing sessionStorage).
  const memoryStorage = new Map();
  function checkoutStorage(action, key, value) {
    try {
      if (action === 'get') return window.sessionStorage.getItem(key) || memoryStorage.get(key) || null;
      if (action === 'set') window.sessionStorage.setItem(key, value);
      else window.sessionStorage.removeItem(key);
    } catch (error) {
      console.warn('[GBJ Shop Pay] checkout storage unavailable', error);
    }
    if (action === 'set') memoryStorage.set(key, value);
    else if (action === 'remove') memoryStorage.delete(key);
    return memoryStorage.get(key) || null;
  }
  const giftThreshold = () => Number(
    window.gbjCartConfig?.giftThreshold ||
    window.rewardSettings?.tiers?.tier1?.threshold_cents ||
    DEFAULT_THRESHOLD_CENTS
  );

  function isGift(item) {
    return Number(item?.variant_id || item?.id) === GIFT_ID ||
      String(item?.properties?.[GIFT_PROPERTY]) === 'true' || String(item?.properties?._gift) === 'true';
  }

  function paidSubtotal(cart) {
    return (cart?.items || [])
      .filter((item) => !isGift(item))
      .reduce((total, item) => total + Number(item.final_line_price ?? item.line_price ?? 0), 0);
  }

  function matchingProductQuantity(cart, variant, properties) {
    return (cart.items || []).filter((item) => !isGift(item) &&
      Number(item.variant_id || item.id) === variant.id &&
      Number(item.selling_plan_allocation?.selling_plan?.id || 0) === Number(variant.sellingPlan || 0) &&
      Object.keys({ ...(item.properties || {}), ...properties }).every((key) =>
        String(item.properties?.[key] || '') === String(properties[key] || '')))
      .reduce((total, item) => total + Number(item.quantity || 0), 0);
  }

  function buttonFromEvent(event) {
    const target = event.target;
    if (target instanceof Element) return target.closest('[data-gbj-checkout]');
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    return path.find((node) => node instanceof Element && node.matches?.('[data-gbj-checkout]')) || null;
  }

  function setBusy(button, busy) {
    button.disabled = busy;
    button.dataset.gbjBusy = busy ? 'true' : 'false';
    if (busy) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
  }

  let cartUiRefresh = null;
  function refreshCartUi() {
    console.info('[GBJ cart sync] refresh requested', { checkoutLocked: Boolean(window.__GBJ_EXPRESS_CHECKOUT_LOCK__), pending: Boolean(cartUiRefresh) });
    // pageshow and visibilitychange can fire together on Safari. Share one
    // refresh so an older response cannot overwrite a newer count.
    if (window.__GBJ_EXPRESS_CHECKOUT_LOCK__) return Promise.resolve();
    if (!cartUiRefresh) {
      cartUiRefresh = restoreCartUi().finally(() => { cartUiRefresh = null; });
    }
    return cartUiRefresh;
  }
  // The theme navigation handler awaits this before displaying a cached drawer.
  window.gbjRefreshCartUi = refreshCartUi;
  window.gbjPrepareCartForCheckout = async function () {
    if (window.__GBJ_EXPRESS_CHECKOUT_LOCK__) throw new Error('Your cart is updating. Please wait.');
    window.__GBJ_EXPRESS_CHECKOUT_LOCK__ = true;
    checkoutDeadline = Date.now() + 20000;
    try {
      await waitForGiftManager();
      let cart = await getCart();
      if (!(cart.items || []).some(item => !isGift(item) && item.quantity > 0)) throw new Error('Your cart is empty. Add a product before checkout.');
      cart = await ensureGift(cart);
      const gifts = (cart.items || []).filter(isGift);
      if (paidSubtotal(cart) >= giftThreshold() && (gifts.length !== 1 || gifts[0].quantity !== 1 || Number(gifts[0].variant_id || gifts[0].id) !== GIFT_ID)) throw new Error('The free gift could not be verified. Please try again.');
      updateCartCount(cart);
      return cart;
    } finally {
      checkoutDeadline = 0;
      window.__GBJ_EXPRESS_CHECKOUT_LOCK__ = false;
    }
  };

  function updateCartCount(cart) {
    if (!Number.isInteger(cart?.item_count) || cart.item_count < 0) return;
    document.querySelectorAll('cart-count.header__cart-count').forEach(badge => {
      badge.textContent = String(cart.item_count);
      badge.style.display = cart.item_count ? '' : 'none';
      badge.classList.toggle('hidden', cart.item_count === 0);
    });
    const header = document.querySelector('#cart-icon-bubble');
    if (!header) return;
    let bubble = header.querySelector('.cart-count-bubble');
    if (cart.item_count === 0) {
      bubble?.remove();
      return;
    }
    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      header.appendChild(bubble);
    }
    let visibleCount = bubble.querySelector('[aria-hidden="true"]');
    if (cart.item_count < 100) {
      if (!visibleCount) {
        visibleCount = document.createElement('span');
        visibleCount.setAttribute('aria-hidden', 'true');
        bubble.appendChild(visibleCount);
      }
      visibleCount.textContent = String(cart.item_count);
    } else visibleCount?.remove();
    const accessibleCount = bubble.querySelector('.visually-hidden');
    if (accessibleCount) accessibleCount.textContent = accessibleCount.textContent.replace(/\d+/, String(cart.item_count));
    else {
      const count = document.createElement('span');
      count.className = 'visually-hidden';
      count.textContent = `${cart.item_count} items in cart`;
      bubble.appendChild(count);
    }
  }

  async function restoreCartUi() {
    try {
      const sectionsUrl = `${cartUrl('cart')}?sections=cart-drawer,cart-icon-bubble&sections_url=${encodeURIComponent(window.location.pathname)}&gbj_return=${Date.now()}`;
      const [sectionResult, cartResult] = await Promise.allSettled([requestJson(sectionsUrl), getCart()]);
      // A failed section must not discard a successful authoritative cart count.
      const sections = sectionResult.status === 'fulfilled' ? sectionResult.value : null;
      const cart = cartResult.status === 'fulfilled' ? cartResult.value : null;
      if (sectionResult.status === 'rejected') console.warn('[GBJ Shop Pay] return sections unavailable', sectionResult.reason);
      if (cartResult.status === 'rejected') console.warn('[GBJ Shop Pay] return count unavailable; keeping existing count', cartResult.reason);
      if (window.__GBJ_EXPRESS_CHECKOUT_LOCK__) return;
      const parser = new DOMParser();

      if (sections?.['cart-drawer']) {
        const documentFragment = parser.parseFromString(sections['cart-drawer'], 'text/html');
        const freshDrawerRoot = documentFragment.querySelector('cart-drawer');
        const currentDrawerRoot = document.querySelector('cart-drawer');
        if (freshDrawerRoot && currentDrawerRoot) {
          // Preserve the upgraded custom element and open state, but replace all stale contents.
          currentDrawerRoot.innerHTML = freshDrawerRoot.innerHTML;
          if (cart) {
            currentDrawerRoot.classList.toggle('is-empty', cart.item_count === 0);
            currentDrawerRoot.querySelector('cart-drawer-items')?.classList.toggle('is-empty', cart.item_count === 0);
          }
        }
      }

      if (sections?.['cart-icon-bubble']) {
        const documentFragment = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
        // The section returns its contents inside .shopify-section, not the
        // header's #cart-icon-bubble anchor. Preserve that anchor and listeners.
        const freshBubble = documentFragment.querySelector('#cart-icon-bubble') ||
          documentFragment.querySelector('.shopify-section');
        const currentBubble = document.querySelector('#cart-icon-bubble');
        if (freshBubble && currentBubble) currentBubble.innerHTML = freshBubble.innerHTML;
      }

      updateCartCount(cart);
      window.moveGiftToTop?.();
      // Product-info listens to the theme pub/sub, not our custom DOM event.
      // Refresh its server-rendered "in cart" quantity/rules on return as well.
      document.querySelectorAll('product-info').forEach((info) => {
        if (info.quantityInput && info.quantityForm) info.fetchQuantityRules?.();
      });

      document.dispatchEvent(new CustomEvent('gbj:cart-sections-refreshed'));
      console.info('[GBJ Shop Pay] return cart restored', {
        itemCount: cart?.item_count ?? null,
        totalPrice: cart?.total_price ?? null
      });
    } catch (error) {
      console.warn('[GBJ Shop Pay] return cart refresh failed', error);
    }
  }

  function readExpressItem() {
    try {
      const item = JSON.parse(checkoutStorage('get', EXPRESS_ITEM_KEY) || 'null');
      if (!item || Date.now() - Number(item.at || 0) > 15 * 60 * 1000) return null;
      return item;
    } catch (_) {
      return null;
    }
  }

  function resetButtons(event) {
    window.__GBJ_EXPRESS_CHECKOUT_LOCK__ = false;
    document.querySelectorAll('[data-gbj-checkout]').forEach((button) => setBusy(button, false));
    const navigation = window.performance?.getEntriesByType?.('navigation')?.[0];
    const returnedByHistory = Boolean(event?.persisted || navigation?.type === 'back_forward');
    // A memory-cache restore revives all old drawer HTML and listeners. Start
    // from fresh server HTML instead of allowing competing old renderers to
    // display an empty cart. Reload's pageshow is not persisted: no loop.
    if (event?.persisted) {
      checkoutStorage('remove', RETURN_RELOAD_KEY);
      window.location.reload();
      return;
    }
    if (returnedByHistory && checkoutStorage('get', RETURN_RELOAD_KEY) === '1') {
      checkoutStorage('remove', RETURN_RELOAD_KEY);
      window.location.reload();
      return;
    }
    // Any cached page can have an old header/drawer, including collections
    // visited before a normal Add to cart (no express-checkout marker).
    if (returnedByHistory || (navigation?.type === 'reload' && readExpressItem())) {
      window.__GBJ_RETURNED_FROM_EXPRESS_CHECKOUT__ = true;
      refreshCartUi();
    }
  }

  window.addEventListener('pageshow', resetButtons);
  // Fresh navigation can also contain stale drawer HTML. Always refresh when
  // the drawer is opened, without changing the cart or opening it again.
  document.addEventListener('gbj:drawer-opened', refreshCartUi);
  // Do not depend solely on the upgraded drawer's open() implementation.
  document.addEventListener('click', (event) => {
    if (event.target instanceof Element && event.target.closest('#cart-icon-bubble, [data-cart-drawer-toggle], [data-cart-toggle]')) {
      refreshCartUi();
    }
  }, true);
  document.addEventListener('visibilitychange', () => {
    if (
      document.visibilityState === 'visible' &&
      window.__GBJ_RETURNED_FROM_EXPRESS_CHECKOUT__
    ) {
      refreshCartUi();
    }
  });

  function showError(form, message) {
    const productForm = form?.closest?.('product-form');
    const wrapper = productForm?.querySelector('.product-form__error-message-wrapper');
    const text = wrapper?.querySelector('.product-form__error-message');

    if (wrapper && text) {
      text.textContent = message;
      wrapper.hidden = false;
      wrapper.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
    } else {
      window.alert(message);
    }
  }

  async function requestJson(url, options = {}) {
    const requestStartedAt = Date.now();
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const remaining = checkoutDeadline ? checkoutDeadline - Date.now() : REQUEST_TIMEOUT_MS;
    if (remaining <= 0) throw new Error('Checkout verification timed out. Please try again.');
    let timeout;
    const timeoutPromise = new Promise((_, reject) => {
      timeout = window.setTimeout(() => {
        controller?.abort();
        reject(new Error('The cart request timed out. Please check your connection and try again.'));
      }, Math.min(REQUEST_TIMEOUT_MS, remaining));
    });

    try {
      return await Promise.race([timeoutPromise, (async () => {
      const response = await fetch(url, {
        credentials: 'same-origin',
        cache: 'no-store',
        ...options,
        signal: controller?.signal,
        headers: {
          Accept: 'application/json',
          ...(options.headers || {})
        }
      });
      console.info('[GBJ checkout timing] cart response', {
        path: String(url).split('?')[0],
        method: options.method || 'GET',
        status: response.status,
        milliseconds: Date.now() - requestStartedAt
      });

      if (!response.ok) {
        let message = `Shopify request failed (${response.status})`;
        try {
          const body = await response.clone().json();
          message = body?.description || body?.message || message;
        } catch (_) {}
        throw new Error(message);
      }

      return await response.json();
      })()]);
    } catch (error) {
      if (error?.name === 'AbortError') throw new Error('The cart request timed out. Please check your connection and try again.');
      throw error;
    } finally {
      if (timeout) window.clearTimeout(timeout);
    }
  }

  function getCart() {
    return requestJson(`${cartUrl('cart.js')}?gbj_shop_pay=1&t=${Date.now()}`);
  }

  function addItems(items) {
    return requestJson(`${cartUrl('cart/add.js')}?gbj_shop_pay=1`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    });
  }

  async function updateItems(updates) {
    let result;
    for (const [id, quantity] of Object.entries(updates)) {
      if (!id || !Number.isInteger(quantity) || quantity < 0) {
        throw new Error('Invalid free gift quantity change.');
      }
      result = await requestJson(`${cartUrl('cart/change.js')}?gbj_shop_pay=1`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, quantity })
      });
    }
    return result;
  }

  async function waitForGiftManager() {
    const startedAt = Date.now();
    while (window.freeGiftManager?.isProcessing && Date.now() - startedAt < REQUEST_TIMEOUT_MS) await wait(25);
    if (window.freeGiftManager?.isProcessing) throw new Error('The cart is updating. Please try again in a moment.');
  }

  function collectProperties(form) {
    const properties = {};
    const data = new FormData(form);

    for (const [key, rawValue] of data.entries()) {
      const match = /^properties\[(.+)\]$/.exec(key);
      if (!match || typeof rawValue !== 'string') continue;
      const privateProperty = match[1].startsWith('_');
      const value = privateProperty ? rawValue : rawValue.trim();
      if (value || privateProperty) properties[match[1]] = value;
    }

    const deliveryElement = document.getElementById('delivery-text');
    const deliveryText = (deliveryElement?.innerText || deliveryElement?.textContent || '').replace('Estimate Shipping Time: ', '').replace(', Est ', ' ').trim();
    if (deliveryText) properties['Estimated Delivery'] = deliveryText;
    return properties;
  }

  function selectedVariant(form) {
    const data = new FormData(form);
    const idInput = form.querySelector('.product-variant-id, input[name="id"]');
    const variantId = Number(idInput?.value || data.get('id'));
    const quantityInput = form.elements.namedItem('quantity');
    let quantity = Number(quantityInput?.value ?? data.get('quantity') ?? 1);
    const exhaustedQuantity = Boolean(quantityInput && quantity < 1 &&
      quantityInput.hasAttribute('max') && Number(quantityInput.max) <= 0 &&
      Number(quantityInput.dataset.cartQuantity || 0) > 0);
    if (exhaustedQuantity) throw new Error('The maximum available quantity is already in your cart. Please update your cart before adding more.');
    const variants = JSON.parse(form.querySelector('[data-gbj-variants]')?.textContent || '[]');
    const picker = form.closest('product-info')?.querySelector('variant-selects');
    const optionValues = picker ? Array.from(picker.querySelectorAll('select')).map((select) => select.value) : [];
    const optionVariant = optionValues.length ? variants.find((item) =>
      item.options?.length === optionValues.length && item.options.every((value, index) => value === optionValues[index])) : null;
    const variant = optionVariant || variants.find((item) => Number(item.id) === variantId);

    if (!variant) throw new Error('Please finish selecting the product options before starting checkout.');
    if (!variant.available) throw new Error('The selected variant is currently unavailable.');
    if (!Number.isInteger(quantity) || quantity < 1) throw new Error('Please select a quantity of at least 1.');

    return {
      id: Number(variant.id),
      quantity,
      price: Number(variant.price || 0),
      checkoutExistingOnly: exhaustedQuantity,
      sellingPlan: data.get('selling_plan') || null
    };
  }

  function syncRingSize(form) {
    const sectionId = form.closest('product-form')?.dataset.sectionId;
    if (window['ringSizeRequired_' + sectionId] === true && window['ringSizeSelected_' + sectionId] !== true) throw new Error('Please select a ring size.');
    if (window['skipMetalTone_' + sectionId]) {
      form.querySelectorAll('input[name="properties[Metal Tone]"]').forEach(input => input.remove());
    } else if (window['metalToneRequired_' + sectionId] === true && window['metalToneSelected_' + sectionId] !== true) {
      throw new Error('Please select a metal tone.');
    }
  }

  async function normalizeGift(cart) {
    const giftItems = (cart.items || []).filter(isGift);
    const totalQuantity = giftItems.reduce((total, item) => total + Number(item.quantity || 0), 0);
    if (giftItems.length <= 1 && totalQuantity === 1) return cart;

    const updates = {};
    giftItems.forEach((item, index) => {
      if (item.key) updates[item.key] = index === 0 ? 1 : 0;
    });
    if (Object.keys(updates).length) {
      const updatedCart = await updateItems(updates);
      // change.js returns the authoritative complete cart. Do not wait for
      // another GET after a successful mutation, unless that response is invalid.
      return Array.isArray(updatedCart?.items) ? updatedCart : getCart();
    }
    return getCart();
  }

  async function ensureGift(cart) {
    const eligible = paidSubtotal(cart) >= giftThreshold();
    let giftItems = (cart.items || []).filter(isGift);

    if (!eligible) {
      if (giftItems.length) {
        const updates = {};
        giftItems.forEach((item) => { if (item.key) updates[item.key] = 0; });
        const updatedCart = Object.keys(updates).length ? await updateItems(updates) : null;
        cart = Array.isArray(updatedCart?.items) ? updatedCart : await getCart();
      }
      return cart;
    }

    if (!giftItems.some((item) => Number(item.variant_id || item.id) === GIFT_ID)) {
      await addItems([{ id: GIFT_ID, quantity: 1, properties: { [GIFT_PROPERTY]: 'true' } }]);
      cart = await getCart();
    }

    return normalizeGift(cart);
  }

  function redirectToCheckout(payment, button) {
    if (FORCE_STANDARD_CHECKOUT_TEST) payment = 'standard';
    const url = new URL(cartUrl('checkout'), window.location.origin);
    if (payment === 'standard') {
      url.searchParams.set('auto_redirect', 'false');
      url.searchParams.set('skip_shop_pay', 'true');
    }
    let navigationError = '';
    let framed = false;
    try { framed = window.self !== window.top; } catch (_) { framed = true; }
    const onNavigationError = (event) => {
      navigationError = String(event.message || 'Browser navigation error').slice(0, 180);
    };
    const onPolicyViolation = (event) => {
      if (/form-action|navigate-to/.test(event.violatedDirective || '')) {
        navigationError = `Browser policy: ${event.violatedDirective}`;
      }
    };
    window.addEventListener('error', onNavigationError);
    document.addEventListener('securitypolicyviolation', onPolicyViolation);
    const fallbackTimer = window.setTimeout(() => {
      if (document.visibilityState !== 'visible') return;
      window.__GBJ_EXPRESS_CHECKOUT_LOCK__ = false;
      setBusy(button, false);
      window.removeEventListener('error', onNavigationError);
      document.removeEventListener('securitypolicyviolation', onPolicyViolation);
      showError(button.closest('form'), `Checkout has not opened. [GBJ NAV-1; product/gift verified; frame=${framed ? 'yes' : 'no'}; payment=${payment || 'standard'}; target=${url.pathname}${navigationError ? '; ' + navigationError : '; no browser error reported'}]`);
      console.warn('[GBJ Shop Pay] checkout navigation did not complete');
    }, 20000);
    window.addEventListener('pagehide', () => {
      window.clearTimeout(fallbackTimer);
      window.removeEventListener('error', onNavigationError);
      document.removeEventListener('securitypolicyviolation', onPolicyViolation);
    }, { once: true });
    if (payment === 'shop_pay') url.searchParams.set('payment', 'shop_pay');
    console.info('[GBJ checkout timing] navigation requested', {
      target: url.pathname,
      milliseconds: Date.now() - Number(button.dataset.gbjCheckoutStartedAt || Date.now())
    });
    // Exactly one navigation: a second form submission while Safari is
    // loading checkout can cancel/restart the first navigation.
    window.location.assign(url.toString());
  }

  async function startCheckout(event, button, form) {
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (button.disabled || button.dataset.gbjBusy === 'true') return;
    if (window.__GBJ_EXPRESS_CHECKOUT_LOCK__) return;

    if (!form) {
      showError(null, 'Product form was not found.');
      return;
    }

    setBusy(button, true);
    button.dataset.gbjCheckoutStartedAt = String(Date.now());
    checkoutDeadline = Date.now() + 20000;
    window.__GBJ_EXPRESS_CHECKOUT_LOCK__ = true;

    try {
      syncRingSize(form);
      const variant = selectedVariant(form);
      const properties = collectProperties(form);
      if (Object.keys(properties).length > 25) {
        throw new Error('This product has too many customization fields for express checkout.');
      }

      const paidItem = { id: variant.id, quantity: variant.quantity, properties };
      if (variant.sellingPlan) paidItem.selling_plan = variant.sellingPlan;

      // Read once, then add the paid product and required gift atomically.
      // This is substantially faster than adding and verifying each line separately.
      // A manager cycle that began just before the click must finish first; the
      // shared lock prevents any new cycle from starting while checkout is prepared.
      await waitForGiftManager();
      const beforeCart = await getCart();
      const matchingExistingQuantity = matchingProductQuantity(beforeCart, variant, properties);
      const existingLine = (beforeCart.items || []).find((item) =>
        !isGift(item) && item.key && matchingProductQuantity({ items: [item] }, variant, properties) > 0);
      // Every new deliberate click adds the selected quantity. The busy lock
      // guards rapid double taps; never automatically retry an add request.
      const quantityToAdd = variant.quantity;
      const expectedProductQuantity = matchingExistingQuantity + quantityToAdd;
      paidItem.quantity = quantityToAdd;
      const projectedSubtotal = paidSubtotal(beforeCart) +
        variant.price * quantityToAdd;
      const alreadyHasGift = (beforeCart.items || []).some((item) =>
        Number(item.variant_id || item.id) === GIFT_ID
      );
      const items = existingLine ? [] : [paidItem];
      let mutationCart = null;

      // Updating the actual line key preserves its complete properties and
      // avoids a second row when adding the same configured variant again.
      if (existingLine) {
        mutationCart = await updateItems({ [existingLine.key]: Number(existingLine.quantity) + quantityToAdd });
      }

      if (projectedSubtotal >= giftThreshold() && !alreadyHasGift) {
        items.push({ id: GIFT_ID, quantity: 1, properties: { [GIFT_PROPERTY]: 'true' } });
      }

      if (items.length) {
        await addItems(items);
        mutationCart = null;
      }

      // One fresh server read verifies the atomic add. ensureGift is only a fallback
      // if Shopify discounts or another cart change altered final eligibility.
      let finalCart = Array.isArray(mutationCart?.items) ? mutationCart : await getCart();
      finalCart = await ensureGift(finalCart);
      // Allow a bounded period for Shopify's cart read to reflect an add.
      // Never repeat an add merely because an immediate read is stale.
      for (let attempt = 0; attempt < 3 && matchingProductQuantity(finalCart, variant, properties) < expectedProductQuantity; attempt++) {
        await wait(250);
        finalCart = await getCart();
      }

      // The express lock prevents our gift manager from racing this flow.
      // Do not repeat a successful authoritative cart read unconditionally.
      const finalEligible = paidSubtotal(finalCart) >= giftThreshold();
      const finalGiftQuantity = (finalCart.items || [])
        .filter((item) => Number(item.variant_id || item.id) === GIFT_ID)
        .reduce((total, item) => total + Number(item.quantity || 0), 0);
      const finalProductQuantity = matchingProductQuantity(finalCart, variant, properties);

      console.info('[GBJ Shop Pay] final cart verification', {
        eligible: finalEligible,
        giftQuantity: finalGiftQuantity,
        productQuantity: finalProductQuantity,
        itemCount: finalCart.item_count
      });

      if (finalProductQuantity < expectedProductQuantity) {
        throw new Error('The selected product could not be verified. Checkout has been stopped. Please try again.');
      }
      if (finalEligible && finalGiftQuantity !== 1) {
        throw new Error('The free gift could not be verified. Checkout has been stopped. Please try again.');
      }

      checkoutStorage('set', EXPRESS_ITEM_KEY, JSON.stringify({
        id: variant.id,
        quantity: variant.quantity,
        at: Date.now()
      }));
      checkoutStorage('set', RETURN_RELOAD_KEY, '1');
      // Reconcile the badge from the already verified cart without adding a
      // network request or waiting for drawer rendering before navigation.
      updateCartCount(finalCart);
      checkoutDeadline = 0;
      redirectToCheckout(button.dataset.gbjPayment, button);
    } catch (error) {
      console.error('[GBJ Shop Pay] checkout blocked', error);
      checkoutDeadline = 0;
      window.__GBJ_EXPRESS_CHECKOUT_LOCK__ = false;
      setBusy(button, false);
      showError(form, error instanceof Error ? error.message : 'Checkout could not start. Please try again.');
      // If add succeeded but verification failed, show the real cart without
      // requiring a manual refresh. Do not bypass the gift verification gate.
      refreshCartUi();
    }
  }

  let paymentTermsForm = null;
  const modalCheckoutSelector = '[data-testid="sample-plans-modal-continue-to-checkout-button"]';
  window.addEventListener('click', (event) => {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
    const termsHost = path.find((node) => node instanceof Element && node.matches('shopify-payment-terms'));
    if (termsHost) {
      paymentTermsForm = termsHost.closest('product-info')?.querySelector('product-form form') || null;
    }
    const modalControl = path.find((node) => node instanceof Element &&
      (node.matches(modalCheckoutSelector) || node.closest(modalCheckoutSelector)));
    if (modalControl) {
      const wrapper = modalControl.matches(modalCheckoutSelector) ? modalControl : modalControl.closest(modalCheckoutSelector);
      const button = wrapper.querySelector('button');
      const form = paymentTermsForm?.isConnected ? paymentTermsForm :
        document.querySelector('main product-info product-form form');
      if (!button) return;
      // Stop Shopify's native modal handler before it opens a Shop Pay window.
      // Reuse the same personalization, cart verification and gift protection.
      button.dataset.gbjPayment = 'standard';
      startCheckout(event, button, form);
      const close = wrapper.getRootNode().querySelector?.('[data-testid="authorize-modal-close-button"]');
      if (form && close) close.click();
      return;
    }
    const button = buttonFromEvent(event);
    if (button) startCheckout(event, button, button.closest('form'));
  }, true);

  console.info('[GBJ Shop Pay] mobile-safe verified checkout loaded');
})();
